import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useForm, FormProvider } from 'react-hook-form';
import { format } from 'date-fns';
import * as yup from 'yup';
import { LOCATION_TYPE } from '@features/ComboLocationSelect';
import { ModalType } from '@constants/modal';
import { useTranslation, useYupValidationResolver } from '@hooks';
import { handleApiError } from '@helpers/api';
import { CouponStatus, CouponType } from '@constants/coupons';
import { couponsService } from '@services';
import { alertActions, couponsActions } from '@actions';
import OfferSchedule from './OfferSchedule';
import PrimaryFields from './PrimaryFields';
import Settings from './Settings';
import OfferType from './OfferType';
import { Modal, Content } from './styles';

const EditModal = ({ selectedCoupon, setSelectedCoupon, modal, setModal, brands, rowsPerPage, page }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const CouponsSchema = yup.object().shape({
        title: yup.string().required('validation.required'),
        locationId: yup.string().when(['locationType', 'brandAccess'], {
            is: (locationType, brandAccess) =>
                locationType === LOCATION_TYPE.LOCATION || (locationType === LOCATION_TYPE.BRAND && !brandAccess),
            then: yup.string().required('validation.required'),
            otherwise: yup.string().nullable(),
        }),
        brandId: yup.string().when(['locationType', 'brandAccess'], {
            is: (locationType, brandAccess) => locationType === LOCATION_TYPE.BRAND && brandAccess,
            then: yup.string().required('validation.required'),
            otherwise: yup.string().nullable(),
        }),
        offerEndDate: yup.string().when('offerEndEnabled', {
            is: offerEndEnabled => offerEndEnabled,
            then: yup.string().required('validation.required'),
            otherwise: yup.string().notRequired(),
        }),
        waitPeriodInHours: yup.string().when('waitPeriodEnabled', {
            is: waitPeriodEnabled => waitPeriodEnabled,
            then: yup
                .string()
                .matches(/^\d+(\.\d+)?$/, 'validation.onlyNumbers')
                .required('validation.required'),
            otherwise: yup.string().notRequired(),
        }),
        redemptionPeriodInDays: yup.string().when('redemptionPeriodEnabled', {
            is: redemptionPeriodEnabled => redemptionPeriodEnabled,
            then: yup
                .string()
                .matches(/^\d+(\.\d+)?$/, 'validation.onlyNumbers')
                .required('validation.required'),
            otherwise: yup.string().notRequired(),
        }),
        pin: yup.string().when('pinEnabled', {
            is: pinEnabled => pinEnabled,
            then: yup.string().min(4, 'validation.required').required('validation.required'),
            otherwise: yup.string().notRequired(),
        }),
        coverPhotoUrl: yup.string().when('coverPhotoEnabled', {
            is: coverPhotoEnabled => coverPhotoEnabled,
            then: yup.string().required('validation.required'),
            otherwise: yup.string().notRequired(),
        }),
        percentageOff: yup.string().when('type', {
            is: type => type === CouponType.PercentageOff,
            then: yup
                .string()
                .matches(/^\d+(\.\d+)?$/, 'validation.onlyNumbers')
                .required('validation.required'),
            otherwise: yup.string().notRequired(),
        }),
        amountOff: yup.string().when('type', {
            is: type => type === CouponType.AmountOff,
            then: yup
                .string()
                .matches(/^\d+(\.\d+)?$/, 'validation.onlyNumbers')
                .required('validation.required'),
            otherwise: yup.string().notRequired(),
        }),
        freeItem: yup.string().when('type', {
            is: type => type === CouponType.FreeItem,
            then: yup.string().required('validation.required'),
            otherwise: yup.string().notRequired(),
        }),
        minimumAmountCurrency: yup.string().required('validation.required'),
    });

    const resolver = useYupValidationResolver(CouponsSchema);

    const {
        getValues,
        setValue,
        control,
        watch,
        handleSubmit,
        clearErrors,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm({
        mode: 'all',
        resolver,
        defaultValues: {
            title: '',
            locationId: '',
            brandId: '',
            locationType: LOCATION_TYPE.BRAND,
            brandAccess: false,
            status: Boolean(CouponStatus.Paused),
            offerEndEnabled: false,
            redemptionPeriodEnabled: false,
            waitPeriodEnabled: false,
            redemptionPeriodInDays: '',
            waitPeriodInHours: '',
            offerEndDate: '',
            pinEnabled: false,
            pin: '',
            coverPhotoEnabled: false,
            coverPhotoUrl: '',
            type: CouponType.PercentageOff,
            percentageOff: '',
            amountOff: '',
            freeItem: '',
            minimumAmountCurrency: 'USD',
            minimumAmount: '',
        },
    });

    useEffect(() => {
        if (modal.type !== ModalType.Create) {
            setValue('title', selectedCoupon.title, { shouldValidate: true });
            setValue('status', Boolean(selectedCoupon.status));
            setValue('offerEndEnabled', selectedCoupon.offerEndEnabled, { shouldValidate: true });
            setValue('redemptionPeriodEnabled', selectedCoupon.redemptionPeriodEnabled, { shouldValidate: true });
            setValue('waitPeriodEnabled', selectedCoupon.waitPeriodEnabled, { shouldValidate: true });
            setValue('redemptionPeriodInDays', selectedCoupon.redemptionPeriodInDays || '', { shouldValidate: true });
            setValue('waitPeriodInHours', selectedCoupon.waitPeriodInHours || '', { shouldValidate: true });
            setValue('offerEndDate', selectedCoupon.offerEndDate ? new Date(selectedCoupon.offerEndDate) : '', {
                shouldValidate: true,
            });
            setValue('pinEnabled', selectedCoupon.pinEnabled, { shouldValidate: true });
            setValue('pin', selectedCoupon.pin ? `${selectedCoupon.pin}` : '', { shouldValidate: true });
            setValue('coverPhotoEnabled', selectedCoupon.coverPhotoEnabled, { shouldValidate: true });
            setValue('coverPhotoUrl', selectedCoupon.coverPhotoUrl || '', { shouldValidate: true });
            setValue('type', selectedCoupon.type, { shouldValidate: true });
            setValue('percentageOff', selectedCoupon.percentageOff || '', { shouldValidate: true });
            setValue('amountOff', selectedCoupon.amountOff || '', { shouldValidate: true });
            setValue('freeItem', selectedCoupon.freeItem || '', { shouldValidate: true });
            setValue('minimumAmountCurrency', selectedCoupon.minimumAmountCurrency || 'USD', { shouldValidate: true });
            setValue('minimumAmount', selectedCoupon.minimumAmount || '', { shouldValidate: true });
        }
    }, [selectedCoupon, modal.type, setValue]);

    const onClose = () => {
        setSelectedCoupon(null);
        setModal({ open: false, type: ModalType.Create });
    };

    const RESPONSE_MSG = {
        [ModalType.Edit]: {
            success: t('alertMessages.editSuccess'),
            error: t('alertMessages.editFail'),
        },
        [ModalType.Create]: {
            success: t('alertMessages.createSuccess'),
            error: t('alertMessages.createFail'),
        },
    };

    const onSubmit = async () => {
        const values = getValues();
        try {
            await couponsService.createOrUpdateCoupon({
                ...(modal.type === ModalType.Edit && { id: selectedCoupon?.id }),
                ...values,
                amountOff: values.amountOff ? values.amountOff : null,
                percentageOff: values.percentageOff ? values.percentageOff : null,
                minimumAmount: values.minimumAmount ? values.minimumAmount : null,
                status: values.status ? CouponStatus.Active : CouponStatus.Paused,
                offerEndDate: values.offerEndDate ? format(new Date(values.offerEndDate), 'yyyy-MM-dd') : '',
            });
            dispatch(couponsActions.get(page, rowsPerPage));
            dispatch(alertActions.success(RESPONSE_MSG[modal.type].success));
            onClose();
        } catch (error) {
            handleApiError(error, RESPONSE_MSG[modal.type].error);
        }
    };

    const isView = modal.type === ModalType.View;

    return (
        <Modal
            isOpen={modal.open}
            handleClose={onClose}
            title={
                modal.type === ModalType.Create
                    ? t('CouponsPage.createCoupon')
                    : modal.type === ModalType.View
                    ? t('CouponsPage.viewCoupon')
                    : t('CouponsPage.editCoupon')
            }
            onPrimaryAction={modal.type === ModalType.View ? null : handleSubmit(onSubmit)}
            primaryActionText={modal.type === ModalType.Edit ? t('buttons.saveChanges') : t('CouponsPage.createCoupon')}
            primaryActionDisabled={isSubmitting || !isValid || !isDirty || isView}
            onSecondaryAction={onClose}
            secondaryActionText={t('buttons.cancel')}
        >
            <FormProvider {...{ getValues, setValue, control, watch, clearErrors }}>
                <Content>
                    <PrimaryFields isView={isView} selectedCoupon={selectedCoupon} brands={brands} />
                    <OfferType isView={isView} />
                    <OfferSchedule isView={isView} />
                    <Settings isView={isView} />
                </Content>
            </FormProvider>
        </Modal>
    );
};

export default EditModal;
